import { useEffect } from "react";
import { useForm, FormProvider, useFormContext } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import Button from "components/common/Button";
import Dialog, { DialogTitle, DialogContent, DialogActions } from "components/common/Dialog";
import Select from "components/common/Select";
import Text from "components/common/Text";
import PhoneInput from "components/common/PhoneInput";
import EditCompanyInfoSchema from "validations/EditCompanyInfoSchema";

function RenderInputFields({ fields }) {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const groupedFields = [];
  for (let i = 0; i < fields.length; i += 2) {
    groupedFields.push(fields.slice(i, i + 2));
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      {groupedFields.map((fieldRow, index) => {
        const isLastSingleField = index === groupedFields.length - 1 && fieldRow.length === 1;

        return (
          <div
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            style={{
              display: "grid",
              gridTemplateColumns: isLastSingleField ? "1fr" : "1fr 1fr",
              gap: "1rem",
              alignItems: "flex-end",
            }}
          >
            {fieldRow.map((field) => {
              const { title, name, type, isRequired, dropdownValues, placeholder } = field;

              if (type === "select") {
                return (
                  <div key={name} style={isLastSingleField ? { gridColumn: "1 / -1" } : {}}>
                    <Select
                      name={name}
                      size="small"
                      label={title}
                      control={control}
                      rules={{ required: isRequired }}
                      placeholder={placeholder || "Select"}
                      options={dropdownValues}
                      error={!!errors[name]}
                      helperText={errors[name]?.message}
                      paperWidth="17rem"
                    />
                  </div>
                );
              }

              if (type === "number" && name === "contactNumber") {
                return (
                  <div key={name} style={isLastSingleField ? { gridColumn: "1 / -1" } : {}}>
                    <PhoneInput
                      name={name}
                      label={title}
                      control={control}
                      rules={{ required: isRequired }}
                      error={!!errors[name]}
                      helperText={errors[name]?.message}
                    />
                  </div>
                );
              }

              if (type === "number" && name === "zip") {
                return (
                  <div key={name} style={isLastSingleField ? { gridColumn: "1 / -1" } : {}}>
                    <Text
                      name={name}
                      label={title}
                      type="number"
                      control={control}
                      rules={{ required: isRequired }}
                      error={!!errors[name]}
                      helperText={errors[name]?.message}
                    />
                  </div>
                );
              }

              return (
                <div key={name} style={isLastSingleField ? { gridColumn: "1 / -1" } : {}}>
                  <Text
                    name={name}
                    label={title}
                    control={control}
                    rules={{ required: isRequired }}
                    error={!!errors[name]}
                    helperText={errors[name]?.message}
                  />
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

export default function EditCompanyInfoModal({
  title = "",
  open = false,
  handleModalClose = () => {},
  data = [],
  handleSave = () => {},
  handleInputChange = () => {},
  editCompanyInfo = false,
}) {
  const defaultValues = data.reduce((acc, curr) => {
    acc[curr.name] = curr.value || "";
    return acc;
  }, {});

  const methods = useForm({
    mode: "onChange",
    defaultValues,
    resolver: editCompanyInfo ? yupResolver(EditCompanyInfoSchema) : undefined,
  });

  const { handleSubmit, formState, watch } = methods;
  const { isValid } = formState;

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name) {
        handleInputChange(name, value[name]);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, handleInputChange, data]);

  const onSubmit = () => {
    handleSave();
  };

  return (
    <Dialog
      open={open}
      onClose={handleModalClose}
      aria-labelledby="edit-company-info-title"
      size="sm"
      title={<DialogTitle title={title} />}
      content={
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} id="company-info-form">
            <DialogContent>
              <RenderInputFields fields={data} />
            </DialogContent>
          </form>
        </FormProvider>
      }
      actions={
        <DialogActions
          action1={<Button variant="text" onClick={handleModalClose} label="Cancel" size="large" />}
          action2={<Button type="submit" form="company-info-form" isDisabled={!isValid} label="Save" size="large" />}
        />
      }
    />
  );
}
